import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  ExternalLink,
  Headphones,
  Loader2,
  Mic,
  PhoneCall,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import {
  VoiceAiService,
  isAgentOnDuty,
  newTransferAgent,
  pickOnDutyAgent,
} from '../../services/voiceAiService';

const ACTIONS = [
  { id: 'lookup_caller', label: 'Lookup caller' },
  { id: 'save_rsa_slot', label: 'Save slot' },
  { id: 'create_rsa_job', label: 'Create job' },
  { id: 'job_status', label: 'Job status' },
  { id: 'cancel_rsa_job', label: 'Cancel job' },
  { id: 'escalate_human', label: 'Escalate human' },
];

function copyText(text) {
  return navigator.clipboard.writeText(text);
}

export default function VoiceAiSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [flash, setFlash] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testField, setTestField] = useState('vehicle');
  const [testValue, setTestValue] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);

  const brainUrl = VoiceAiService.brainUrl();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [settings, wa, sess, voiceJobs] = await Promise.all([
        VoiceAiService.getSettings(),
        VoiceAiService.listWaAccounts(),
        VoiceAiService.listSessions(),
        VoiceAiService.listJobs(),
      ]);
      setForm({
        ...settings,
        omnidim_api_key: settings.omnidim_api_key_masked || '',
      });
      setAccounts(wa);
      setSessions(sess);
      setJobs(voiceJobs);
    } catch (e) {
      setFlash(e.message || 'Failed to load Voice AI');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const show = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(''), 3500);
  };

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await VoiceAiService.saveSettings(form);
      show('Voice AI settings saved');
      await loadAll();
    } catch (e) {
      show(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const applyOmni = async () => {
    if (!form) return;
    if (!String(form.omnidim_api_key || '').trim()) {
      show('Paste OmniDimension API key, Save, then Apply');
      return;
    }
    setSyncBusy(true);
    try {
      await VoiceAiService.saveSettings({
        ...form,
        omnidim_agent_id: form.omnidim_agent_id || '258948',
      });
      const res = await VoiceAiService.applyOmniDimension({ secret: form.webhook_secret });
      const n = (res.integrations || []).filter((i) => i.attached).length;
      show(`Agent ${res.agent_id} updated · ${n} Custom APIs attached`);
      await loadAll();
    } catch (e) {
      show(e.message || 'OmniDimension apply failed');
    } finally {
      setSyncBusy(false);
    }
  };

  const rotateSecret = async () => {
    if (!window.confirm('Rotate webhook secret? Update OmniDimension Custom API header after this.')) return;
    try {
      const secret = await VoiceAiService.rotateWebhookSecret();
      patch({ webhook_secret: secret });
      show('New webhook secret saved — paste it into OmniDimension');
    } catch (e) {
      show(e.message || 'Rotate failed');
    }
  };

  const runTool = async (action) => {
    const phone = testPhone.replace(/\D/g, '').slice(-10);
    if (phone.length !== 10 && action !== 'escalate_human') {
      show('Enter a 10-digit test phone');
      return;
    }
    setTestBusy(true);
    setTestResult(null);
    try {
      const extra = action === 'escalate_human' ? { reason: 'admin_test' } : {};
      const res = await VoiceAiService.callTool({
        action,
        phone,
        field: action === 'save_rsa_slot' ? testField : undefined,
        value: action === 'save_rsa_slot' ? testValue : undefined,
        secret: form?.webhook_secret,
        extra,
      });
      setTestResult(res);
      if (res.ok) await loadAll();
    } catch (e) {
      setTestResult({ ok: false, status: 0, body: { error: e.message } });
    } finally {
      setTestBusy(false);
    }
  };

  const customApis = useMemo(
    () =>
      ACTIONS.map((a) => ({
        ...a,
        body: JSON.stringify(
          a.id === 'save_rsa_slot'
            ? { action: a.id, phone: '{{caller_phone}}', field: '{{field}}', value: '{{value}}' }
            : a.id === 'escalate_human'
              ? { action: a.id, phone: '{{caller_phone}}', reason: '{{reason}}' }
              : a.id === 'job_status'
                ? { action: a.id, phone: '{{caller_phone}}', want_otp: '{{want_otp}}' }
                : { action: a.id, phone: '{{caller_phone}}' },
          null,
          2,
        ),
      })),
    [],
  );

  if (loading && !form) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <RefreshCw className="animate-spin text-violet-600" size={36} />
        <p className="text-sm text-slate-500 font-medium">Loading Voice AI…</p>
        {flash ? <p className="text-sm text-red-600 max-w-lg text-center">{flash}</p> : null}
      </div>
    );
  }

  if (!form) {
    return (
      <div className="p-8 text-sm text-red-700">
        {flash || 'Voice AI settings table missing. Run supabase/voice-ai.sql in the SQL editor.'}
      </div>
    );
  }

  const liveAgent = pickOnDutyAgent(form.transfer_agents || []);

  return (
    <div className="w-full space-y-6 pb-12">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-slate-200/80 pb-6">
        <div className="flex items-start gap-4">
          <button
            type="button"
            onClick={() => navigate('/more')}
            className="mt-1 p-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Mic className="text-violet-600" size={22} />
              <h1 className="text-2xl font-bold text-slate-900">Voice AI</h1>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              OmniDimension voice layer + RoadServe RSA brain. Location and confirmation go on WhatsApp like B2B.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/voice-ai-recordings')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-800 text-sm hover:bg-slate-50"
          >
            <Headphones size={16} />
            Call recordings
          </button>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={!!form.enabled}
              onChange={(e) => patch({ enabled: e.target.checked })}
            />
            Master Voice AI ON
          </label>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save
          </button>
        </div>
      </div>

      {flash ? (
        <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm text-violet-900">{flash}</div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
          <h2 className="font-semibold text-slate-900">Welcome + transfer agents</h2>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Spoken welcome</label>
            <textarea
              rows={3}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={form.welcome_message || ''}
              onChange={(e) => patch({ welcome_message: e.target.value })}
            />
          </div>
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase">Human transfer (IST)</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Escalate isi list ke on-duty number par jayega. 24h agent hamesha available. Timing overnight bhi chalega (jaise 20:00–08:00). Pehla on-duty agent (list order) pick hota hai.
                </p>
              </div>
              {liveAgent ? (
                  <span className="shrink-0 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-1 text-[11px] font-medium">
                    Abhi: {liveAgent.name || liveAgent.phone}
                  </span>
                ) : (
                  <span className="shrink-0 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 text-[11px] font-medium">
                    Koi on-duty nahi
                  </span>
                )}
            </div>
            {(form.transfer_agents || []).map((agent, i) => (
              <div key={agent.id || i} className="rounded-lg border border-slate-100 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    className="flex-1 border rounded-lg px-3 py-2 text-sm"
                    placeholder="Agent name"
                    value={agent.name || ''}
                    onChange={(e) => {
                      const next = [...(form.transfer_agents || [])];
                      next[i] = { ...agent, name: e.target.value };
                      patch({ transfer_agents: next });
                    }}
                  />
                  <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={agent.enabled !== false}
                      onChange={(e) => {
                        const next = [...(form.transfer_agents || [])];
                        next[i] = { ...agent, enabled: e.target.checked };
                        patch({ transfer_agents: next });
                      }}
                    />
                    On
                  </label>
                  <button
                    type="button"
                    className="p-2 rounded-lg border text-slate-500 hover:bg-red-50 hover:text-red-600"
                    onClick={() => patch({ transfer_agents: (form.transfer_agents || []).filter((_, j) => j !== i) })}
                    aria-label="Remove agent"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  placeholder="10-digit number"
                  value={agent.phone || ''}
                  onChange={(e) => {
                    const next = [...(form.transfer_agents || [])];
                    next[i] = { ...agent, phone: e.target.value };
                    patch({ transfer_agents: next });
                  }}
                />
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                    <input
                      type="checkbox"
                      checked={!!agent.always_on}
                      onChange={(e) => {
                        const next = [...(form.transfer_agents || [])];
                        next[i] = { ...agent, always_on: e.target.checked };
                        patch({ transfer_agents: next });
                      }}
                    />
                    24 hours
                  </label>
                  {agent.always_on ? (
                    <span className="text-xs text-slate-500">Har time isi number par forward</span>
                  ) : (
                    <>
                      <label className="text-xs text-slate-500">
                        From
                        <input
                          type="time"
                          className="ml-1.5 border rounded-lg px-2 py-1 text-sm"
                          value={agent.start || '09:00'}
                          onChange={(e) => {
                            const next = [...(form.transfer_agents || [])];
                            next[i] = { ...agent, start: e.target.value };
                            patch({ transfer_agents: next });
                          }}
                        />
                      </label>
                      <label className="text-xs text-slate-500">
                        To
                        <input
                          type="time"
                          className="ml-1.5 border rounded-lg px-2 py-1 text-sm"
                          value={agent.end || '21:00'}
                          onChange={(e) => {
                            const next = [...(form.transfer_agents || [])];
                            next[i] = { ...agent, end: e.target.value };
                            patch({ transfer_agents: next });
                          }}
                        />
                      </label>
                    </>
                  )}
                  {isAgentOnDuty(agent) ? (
                    <span className="text-[11px] font-medium text-emerald-700">On duty now</span>
                  ) : null}
                </div>
              </div>
            ))}
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700"
              onClick={() => patch({ transfer_agents: [...(form.transfer_agents || []), newTransferAgent()] })}
            >
              <Plus size={14} /> Add agent
            </button>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Location wait (seconds)</label>
            <input
              type="number"
              min={30}
              max={180}
              className="w-40 border rounded-lg px-3 py-2 text-sm"
              value={form.location_wait_seconds || 90}
              onChange={(e) => patch({ location_wait_seconds: Number(e.target.value) })}
            />
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-semibold text-slate-900">OmniDimension</h2>
            <button
              type="button"
              onClick={applyOmni}
              disabled={syncBusy}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-50"
            >
              {syncBusy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
              Apply to OmniDimension
            </button>
          </div>
          <p className="text-xs text-slate-500">
            Code tab on omnidim.io is a snapshot, not an editor. This button PUTs agent 258948
            (welcome, transfer, custom-API transfer, prompt) and attaches the 5 RoadServe tools.
          </p>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">API key</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              value={form.omnidim_api_key || ''}
              onChange={(e) => patch({ omnidim_api_key: e.target.value })}
              placeholder="Paste key — leave masked to keep current"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Agent ID</label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                value={form.omnidim_agent_id || ''}
                onChange={(e) => patch({ omnidim_agent_id: e.target.value })}
                placeholder="258948"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">From number ID</label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                value={form.omnidim_from_number_id || ''}
                onChange={(e) => patch({ omnidim_from_number_id: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Inbound DID</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              value={form.inbound_did || ''}
              onChange={(e) => patch({ inbound_did: e.target.value })}
              placeholder="+91…"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Webhook secret (Custom API header)</label>
            <div className="flex gap-2">
              <input
                className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono"
                value={form.webhook_secret || ''}
                onChange={(e) => patch({ webhook_secret: e.target.value })}
              />
              <button
                type="button"
                className="px-3 py-2 border rounded-lg text-sm"
                onClick={() => copyText(form.webhook_secret || '').then(() => show('Secret copied'))}
              >
                <Copy size={14} />
              </button>
              <button type="button" className="px-3 py-2 border rounded-lg text-sm" onClick={rotateSecret}>
                Rotate
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Header: x-voice-ai-secret</p>
          </div>
        </section>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <h2 className="font-semibold text-slate-900">WhatsApp (same as B2B)</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Send from WABA</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
              value={form.wa_account_id || ''}
              onChange={(e) => patch({ wa_account_id: e.target.value || null })}
            >
              <option value="">Auto (7909999427)</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name || 'WhatsApp'} {a.display_phone ? `· ${a.display_phone}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Location template</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              value={form.location_template || 'ask_customer_location'}
              onChange={(e) => patch({ location_template: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Confirm template</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              value={form.confirm_template || 'complaint_registration_confirmation'}
              onChange={(e) => patch({ confirm_template: e.target.value })}
            />
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Custom API URLs</h2>
          <button
            type="button"
            className="text-xs text-violet-700 inline-flex items-center gap-1"
            onClick={() => copyText(brainUrl).then(() => show('Brain URL copied'))}
          >
            <Copy size={12} /> {brainUrl}
          </button>
        </div>
        <p className="text-xs text-slate-500">
          Method POST. Header <code className="font-mono">x-voice-ai-secret</code>. Phone must be the caller ID.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {customApis.map((api) => (
            <div key={api.id} className="rounded-lg border border-slate-100 p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium">{api.label}</p>
                <button
                  type="button"
                  className="text-xs text-slate-500"
                  onClick={() => copyText(api.body).then(() => show(`${api.id} body copied`))}
                >
                  Copy body
                </button>
              </div>
              <pre className="text-[11px] bg-slate-50 rounded p-2 overflow-x-auto">{api.body}</pre>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <h2 className="font-semibold text-slate-900">Test tools</h2>
        <p className="text-xs text-slate-500">Runs even if Master is OFF (admin_test). Turn Master ON before live calls.</p>
        <div className="flex flex-wrap gap-2">
          <input
            className="border rounded-lg px-3 py-2 text-sm font-mono w-40"
            placeholder="10-digit phone"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
          />
          <select
            className="border rounded-lg px-3 py-2 text-sm"
            value={testField}
            onChange={(e) => setTestField(e.target.value)}
          >
            {['vehicle', 'problem', 'fuel', 'whatsapp', 'pickup', 'pickup_check', 'drop', 'drop_check'].map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          <input
            className="border rounded-lg px-3 py-2 text-sm flex-1 min-w-[160px]"
            placeholder="value (for save slot)"
            value={testValue}
            onChange={(e) => setTestValue(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {ACTIONS.map((a) => (
            <button
              key={a.id}
              type="button"
              disabled={testBusy}
              onClick={() => runTool(a.id)}
              className="px-3 py-2 rounded-lg border text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {a.label}
            </button>
          ))}
        </div>
        {testBusy ? <Loader2 className="animate-spin text-violet-600" size={18} /> : null}
        {testResult ? (
          <pre className="text-[11px] bg-slate-50 rounded p-3 overflow-x-auto max-h-64">
            {JSON.stringify(testResult, null, 2)}
          </pre>
        ) : null}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">Open sessions</h2>
            <button type="button" onClick={loadAll} className="text-xs text-slate-500 inline-flex items-center gap-1">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {sessions.length === 0 ? (
              <p className="text-sm text-slate-500">No sessions yet.</p>
            ) : (
              sessions.map((s) => (
                <div key={s.id} className="rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <p className="font-medium">
                    {s.contact_phone} · {s.status}
                  </p>
                  <p className="text-xs text-slate-500">
                    {s.service_type || '—'} · {s.vehicle_model || '—'} · {s.pickup_address || 'no pin'}
                  </p>
                </div>
              ))
            )}
          </div>
        </section>
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">Voice AI jobs</h2>
            <button
              type="button"
              className="text-xs text-violet-700 inline-flex items-center gap-1"
              onClick={() => navigate('/app-enquiries')}
            >
              App Enquiries <ExternalLink size={12} />
            </button>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {jobs.length === 0 ? (
              <p className="text-sm text-slate-500">No voice_ai jobs yet.</p>
            ) : (
              jobs.map((j) => (
                <div key={j.id} className="rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <p className="font-medium flex items-center gap-1">
                    <PhoneCall size={12} /> {j.customer_name || j.contact_number} · {j.service_type}
                  </p>
                  <p className="text-xs text-slate-500">
                    {j.request_status} · {j.assigned_mechanic_name || 'unassigned'}
                  </p>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
